import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, ShieldCheck, Clock, Briefcase, ChevronDown, CreditCard } from 'lucide-react';
import { secureFetch, ROUTES } from "@utils/apiClient";
import { trackLead } from './services/avaAPI';

/**
 * Micheals Infrastructure Advisory - INTERNAL DIAGNOSTIC SUITE
 * ROLE: Triggers the generation of a Forensic Audit PDF and Trello attachment.
 */
export const triggerMockAudit = async () => {
    const mockData = {
        name: "Auditor (Internal Test)",
        email: "info@medconsulting.solutions",
        serviceType: "Forensic System Audit",
        description: "SYSTEM_MOCK_TEST: Verifying PDF generation and Trello attachment synchronization. Protocol 9.7.6."
    };

    console.log("[DIAGNOSTIC]: Initiating Forensic PDF Generation...");

    try {
        const response = await secureFetch(ROUTES.PROXY_INTAKE, {
            method: 'POST',
            body: JSON.stringify(mockData)
        });

        const result = await response.json();

        if (result.success) {
            console.log(`✅ [SUCCESS]: Audit PDF attached to Trello Card: ${result.cardId}`);
        } else {
            console.error("❌ [FAILURE]: Proxy accepted request but Principal Engine failed delivery.");
        }
    } catch (error) {
        console.error("[FORENSIC_ERROR]: Bridge connection refused.", error);
    }
};

const PROJECT_OPTIONS = [
  { 
    group: 'Design & Engineering', 
    labels: [
      'Infrastructure Blueprinting', 
      'Smart Home Logic Design', 
      'Architectural Systems Integration', 
      'EV Infrastructure Strategy'
    ] 
  },
  { 
    group: 'Diagnostic & Consultation', 
    labels: [
      'Strategic Technical Briefing', 
      'System Integrity Forensic Audit', 
      'Diagnostic Logic Analysis',
      'Thermal Imaging & Efficiency Mapping'
    ] 
  },
  { 
    group: 'Project Administration', 
    labels: [
      'Owner Representative Services', 
      'Contractor Specification Audit', 
      'Third-Party Implementation Oversight'
    ] 
  }
];

const ContactForm = ({ sessionId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [startTime] = useState(Date.now());
  
  const [formData, setFormData] = useState({
    name: '',
    email: sessionStorage.getItem('leadEmail') || '',
    projectType: 'Design & Engineering: Infrastructure Blueprinting',
    description: ''
  });

  const isPaidConsult = formData.projectType.includes('System Integrity Forensic Audit') || 
                        formData.projectType.includes('Diagnostic Logic Analysis') || 
                        formData.projectType.includes('Strategic Technical Briefing');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const activeSessionId = sessionId || sessionStorage.getItem('ava_session_id');
    sessionStorage.setItem('leadEmail', formData.email);

    const proxyPayload = {
      name: formData.name,
      email: formData.email,
      serviceType: formData.projectType,
      description: formData.description
    };

    const trackingPayload = {
      leadName: formData.name,
      leadEmail: formData.email,
      primaryInterest: formData.projectType,
      details: formData.description,
      sessionId: activeSessionId, 
      source: 'Institutional Consultation Form',
      userAction: 'FORM_SUBMISSION',
      timestamp: new Date().toISOString(),
      consultationFeeAcknowledged: isPaidConsult,
      estimatedValue: isPaidConsult ? 150 : 0,
      timeOnForm: (Date.now() - startTime) / 1000 
    };

    try {
      const proxyPromise = secureFetch(ROUTES.PROXY_INTAKE, {
        method: 'POST',
        headers: {
          'x-ava-session-id': activeSessionId || ''
        },
        body: JSON.stringify(proxyPayload),
      });

      const avaPromise = (activeSessionId && activeSessionId !== "undefined")
        ? trackLead(trackingPayload) 
        : Promise.resolve({ skipped: true });

      const [proxyResponse] = await Promise.allSettled([proxyPromise, avaPromise]);
      
      if (proxyResponse.status === 'fulfilled' && proxyResponse.value.ok) {
        navigate('/thank-you');
      } else {
        throw new Error("Bridge Transmission Failed");
      }
    } catch (err) {
      console.error("Forensic Error during submission:", err);
      navigate('/thank-you');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-stone-50 scroll-mt-20">
      <div className="max-w-5xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          
          <div className="flex flex-col justify-start pt-4">
            <span 
                className="text-amber-700 uppercase tracking-[0.4em] text-[10px] font-bold mb-6 cursor-pointer"
                onDoubleClick={triggerMockAudit}
            >
              Engagement Request
            </span>
            <h2 className="text-4xl md:text-6xl font-serif text-stone-900 leading-[1.1] mb-8">
              Initiate your <br /> 
              <span className="italic font-light text-stone-500">technical briefing.</span>
            </h2>
            
            <div className="space-y-8">
              <div className="flex gap-6">
                <Briefcase className="text-amber-700 mt-1" size={22} />
                <div>
                  <p className="font-bold text-xs uppercase tracking-[0.2em] text-stone-800">Professional Oversight</p>
                  <p className="text-stone-500 text-base font-light mt-1 italic leading-relaxed">Independent project administration and infrastructure design.</p>
                </div>
              </div>
              <div className="flex gap-6">
                <Clock className="text-amber-700 mt-1" size={22} />
                <div>
                  <p className="font-bold text-xs uppercase tracking-[0.2em] text-stone-800">Standard Review</p>
                  <p className="text-stone-500 text-base font-light mt-1 italic">Direct principal review within 24 business hours.</p>
                </div>
              </div>
              <div className="flex gap-6">
                <CreditCard className="text-amber-700 mt-1" size={22} />
                <div>
                  <p className="font-bold text-xs uppercase tracking-[0.2em] text-stone-800">Consultation Standard</p>
                  <p className="text-stone-500 text-base font-light mt-1 italic">$150 strategy fee for onsite technical audits.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-10 md:p-14 shadow-2xl shadow-stone-200/40 border border-stone-100 rounded-sm">
            <form onSubmit={handleSubmit} className="space-y-12">
              <div className="space-y-10">
                <div className="relative group">
                  <input required type="text" value={formData.name}
                    className="peer w-full border-b border-stone-200 pt-4 pb-3 outline-none focus:border-amber-700 transition-colors bg-transparent text-lg text-stone-800 font-light" 
                    placeholder=" " onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                  <label className="absolute left-0 top-4 text-stone-400 text-sm uppercase tracking-widest transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-4 peer-focus:-top-6 peer-focus:text-[11px] peer-focus:text-amber-700 peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-[11px]">
                    Principal Contact
                  </label>
                </div>

                <div className="relative group">
                  <input required type="email" value={formData.email}
                    className="peer w-full border-b border-stone-200 pt-4 pb-3 outline-none focus:border-amber-700 transition-colors bg-transparent text-lg text-stone-800 font-light" 
                    placeholder=" " onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                  <label className="absolute left-0 top-4 text-stone-400 text-sm uppercase tracking-widest transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-4 peer-focus:-top-6 peer-focus:text-[11px] peer-focus:text-amber-700 peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-[11px]">
                    Secure Email Address
                  </label>
                </div>

                <div className="relative pt-4">
                  <select className="w-full border-b border-stone-200 pb-3 outline-none focus:border-amber-700 bg-transparent text-stone-800 appearance-none cursor-pointer font-medium text-sm"
                    value={formData.projectType} onChange={(e) => setFormData({...formData, projectType: e.target.value})}
                  >
                    {PROJECT_OPTIONS.map(opt => (
                      <optgroup key={opt.group} label={opt.group.toUpperCase()}>
                        {opt.labels.map(label => (
                          <option key={label} value={`${opt.group}: ${label}`}>{label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <label className="absolute -top-4 left-0 text-[11px] text-amber-700 uppercase tracking-[0.2em] font-bold">Inquiry Classification</label>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-stone-400">
                    <ChevronDown size={18} />
                  </div>
                </div>

                <div className="relative pt-4">
                  <textarea required rows="3" value={formData.description}
                    className="peer w-full border-b border-stone-200 pb-1 outline-none focus:border-amber-700 transition-colors bg-transparent resize-none text-stone-800 text-lg leading-relaxed font-light" 
                    placeholder=" " onChange={(e) => setFormData({...formData, description: e.target.value})}
                  ></textarea>
                  <label className="absolute left-0 top-4 text-stone-400 text-sm uppercase tracking-widest transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-4 peer-focus:-top-6 peer-focus:text-[11px] peer-focus:text-amber-700 peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-[11px]">
                    Project Scope & Intent
                  </label>
                </div>
              </div>

              {isPaidConsult && (
                <div className="bg-amber-50/50 p-6 border border-amber-100 flex items-start gap-4 animate-in fade-in slide-in-from-top-2 duration-400 rounded-sm">
                  <CreditCard size={20} className="text-amber-700 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-amber-900 leading-relaxed uppercase tracking-wider font-semibold">
                    Strategic Protocol: Onsite diagnostic audits and technical consultations require a $150 professional fee, fully credited toward project administration upon engagement.
                  </p>
                </div>
              )}

              <div className="flex items-start gap-3 py-4">
                <input type="checkbox" required className="mt-1 border-stone-300 accent-amber-700 cursor-pointer" id="auth" />
                <label htmlFor="auth" className="text-[10px] uppercase tracking-wider text-stone-500 leading-relaxed cursor-pointer">
                  I authorize Micheals Infrastructure Advisory to perform an initial infrastructure intelligence review based on the briefing provided.
                </label>
              </div>

              <button type="submit" disabled={loading}
                className="w-full bg-stone-900 text-white py-6 text-xs uppercase tracking-[0.5em] font-bold hover:bg-amber-700 active:scale-[0.99] transition-all flex items-center justify-center gap-4 disabled:opacity-50 shadow-xl shadow-stone-200"
              >
                {loading ? 'Initializing Forensic Analysis...' : (
                  <>Initiate Technical Briefing <Send size={16} /></>
                )}
              </button>
              
              <div className="flex items-center justify-center gap-3 text-emerald-800">
                <ShieldCheck size={16} />
                <span className="text-[10px] uppercase tracking-[0.2em] font-medium">Transmission Encrypted & Logged</span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;